import { Country, CountryFilterPair, FilterKeys, Filters, Option, PageName, PageType } from '@utils/pageConfig/Types';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { Periodicity } from '@components/time-period-selector/PeriodsTypes';
import { OverviewKpi, ProductAnalyzerGroupDetails, SaleType, SortKeys, SortLabels } from '@models/Products';
import { Dispatch, SetStateAction } from 'react';
import { FocusArea } from '@components/products-top-5-table/Types';
import { getAppliedFiltersFromPagesConfig } from '@utils/FilterUtils';
import { checkPeriodExist, getInitialPeriod, isPeriodYTDOrMAT } from '@utils/PeriodUtils';
import { BaseTimePeriod, MainFilters } from '@services/FiltersTypes';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { SellerInsightsRetailerClientState } from '@state/reducers/seller-insights-retailer-client/SellerInsightsRetailerClientReducer';
import { RetailerClientState } from '@state/reducers/retailer-client/RetailerClientReducer';
import { HitlistsRetailerClientState } from '@state/reducers/hitlist-retailer-client/HitlistsRetailerClientReducer';
import { RelativePeriod } from '@components/comparison-period-selector/Types';
import { WarningPeriodicityOnChangeEvent } from '@components/containers/warning-periodicity/WarningPeriodicityContainer';
import { SideFiltersState } from '@state/reducers/side-filters/SideFiltersReducer';
import { applyTimeFramePicker } from '@components/time-frame-picker/utils/TimeFramePickerUtils';
import { MainFilterStateProducts } from '@state/reducers/main-filter-products/MainFiltersProductsReducer';
import { getInvalidPGs } from '@utils/CountryUtils';
import { NavigationLinkProps } from '@components/tab-nav/TabNav';
import { PERFORMANCE_ANALYSIS_LINE_CHART_WARNING_PERIODICITY_MESSAGE } from '@pages/Utilities';

export const PRODUCTS_YTD_MAT_WARNING_MSG = PERFORMANCE_ANALYSIS_LINE_CHART_WARNING_PERIODICITY_MESSAGE;

export enum ProductType {
  PerformanceAnalyzer = 'PERFORMANCE_ANALYZER',
  ProductAnalyzer = 'PRODUCT_ANALYZER',
  RetailKad = 'RETAIL_KAD',
  MarketPremium = 'MARKET_PREMIUM',
  SellerInsights = 'SELLER_INSIGHTS',
}

export enum ProductDataType {
  PerformanceAnalyzer = 'PERFORMANCE_ANALYZER',
  ProductAnalyzer = 'PRODUCT_ANALYZER',
}

export const isProductAnalyzerSubscription = (productType: ProductType) => {
  return [ProductType.ProductAnalyzer].includes(productType);
};

export const isRetailInsightsOrMarketPremiumSubscription = (productType: ProductType) => {
  return [ProductType.PerformanceAnalyzer, ProductType.MarketPremium].includes(productType);
};

export const isSellerInsightsSubscription = (productType: ProductType) => {
  return [ProductType.SellerInsights].includes(productType);
};

export const isRetailKadSubscription = (productType: ProductType) => {
  return [ProductType.RetailKad].includes(productType);
};

export const countriesToQuery = (
  pagesConfig: PagesConfig,
  appliedCountries: Option[],
  productType: ProductType,
): Country[] => {
  let clientCountries: Country[] = [
    ...pagesConfig.hitlistsClientCountries,
    ...pagesConfig.sellerInsightsClientCountries,
  ];
  if (isRetailKadSubscription(productType)) {
    clientCountries = [...clientCountries, ...pagesConfig.clientCountries];
  }
  return clientCountries.filter((country) =>
    appliedCountries.some((appliedCountry) => country.id === appliedCountry.value),
  );
};

export interface RequestPayloadArgumentObject {
  clientId: number;
  productType: ProductType;
  countryId: string[];
  productGroup: Option | undefined;
  periodType: Periodicity;
  period: string;
  comparisonPeriod: string;
  appliedFilters: Filters[];
  kpi: SaleType;
  calculateRollingPeriod: boolean;
  itemIdList?: string[];
  //Pagination use
  page: number | undefined;
  size: number | undefined;
  //Sorting use
  isMarket: boolean;
  sortBy: string | undefined;
  sortDirection: string | undefined;
  sortKey: string | undefined;
  comparisonGrowth: GrowthTypes | undefined;
  exportCompareComparisonPeriodType?: RelativePeriod | BaseTimePeriod;
  productGrouping?: ProductAnalyzerGroupDetails[];
  isUsingCollectionLiveData?: boolean;
}

export interface ProductsParamArgumentObject {
  productType: ProductType;
  pageNumber: number;
  productGroupParam: any;
  pagesConfig: PagesConfig;
  period: BaseTimePeriod | string;
  mainFilterStateProducts: any;
  retailerClientState: any;
  countriesToQuery: Country[];
  timeFrame: Periodicity;
  defaultPageSize: any;
  selectedSaleType: any;
  sortingKpi: any;
  isMarket: FocusArea | undefined;
  growthType: GrowthTypes | undefined;
  itemIdList?: string[];
  overrideKpi?: SaleType;
  comparisonPeriod: RelativePeriod | BaseTimePeriod;
  productGrouping?: ProductAnalyzerGroupDetails[];
}

export interface ProductsItemFeaturesRequestPayload {
  clientId: number;
  productType: ProductType;
  productDataType: ProductType;
  country: string[];
  periodType: string;
  productGroup: string | undefined;
  itemIds: string[];
}

export interface SaveFilterParam {
  pageType: PageType;
  productType: ProductType;
  name: string;
  country: string;
  clientId: number;
  userId: string;
  periodicity: Periodicity;
  filters: { filterKey: FilterKeys; filterTitle: string; options: any[] }[];
}

export const onTabLinkClickHandler = (
  link: NavigationLinkProps,
  navigate: any,
  setSelectedLink: any,
  basePath: string,
): void => {
  const fullPath = `${basePath}/${link.url}`;
  navigate(fullPath);
  setSelectedLink(link);
};

export const noResultForProductOverviewTab = (overviewKpi: OverviewKpi): boolean => {
  return !overviewKpi?.items?.length;
};

/**
 * To call other APIs when the timeFrame isChanged
 */
export const timeFrameOnChangeHandlerUtils = (
  filter: MainFilters,
  setTimeFrame: Dispatch<SetStateAction<Periodicity>>,
  setPeriod: Dispatch<SetStateAction<BaseTimePeriod>>,
  mainFilterStateProducts: any,
  appliedCountries: Option[],
  event: WarningPeriodicityOnChangeEvent,
  updateMainFiltersProducts: any,
) => {
  const currTimeFrame = event.value;
  const currPeriod = getInitialPeriod(filter, currTimeFrame);

  setTimeFrame(currTimeFrame);
  setPeriod(currPeriod);

  updateMainFiltersProducts({
    timeFrame: currTimeFrame,
    period: currPeriod,
    comparisonPeriod: mainFilterStateProducts.comparisonPeriod,
    selectedGrowthType: mainFilterStateProducts.selectedGrowthType,
    country: appliedCountries[0].value,
  });
};

export const onFiltersTrayClickHandlerUtils = (
  isExpanded: boolean,
  pagesConfig: PagesConfig,
  removeUnsavedFilters: any,
): void => {
  pagesConfig.toggleFiltersTray(isExpanded);
  removeUnsavedFilters(pagesConfig);
};

export const loadingDataPreparation = (
  pagesConfig: PagesConfig,
  isLoading: boolean,
  defaultFilters: MainFilters[],
  timeFrame: Periodicity,
  loadData: any,
  productType: ProductType,
) => {
  // Get the country & pg from pagesConfig to avoid situations where appliedFilters is not updated yet
  const countries = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.COUNTRY, PageType.RI_PRODUCT);
  const productGroup = getAppliedFiltersFromPagesConfig(pagesConfig, FilterKeys.PRODUCT_GROUP, PageType.RI_PRODUCT);
  if ((!isLoading || pagesConfig.isRetailerChanged) && countries && productGroup) {
    const countriesToQueryObject = countriesToQuery(pagesConfig, countries, productType);

    const responseTimeFrame = checkPeriodExist(defaultFilters, countriesToQueryObject[0]?.id, timeFrame);

    loadData(responseTimeFrame, countriesToQueryObject, productGroup[0]);
  }
};

export const getCurrentRetailerClientState = (
  currProdType: ProductType,
  retailerClientState: RetailerClientState,
  hitlistsRetailerClientState: HitlistsRetailerClientState,
  sellerInsightsRetailerClientState: SellerInsightsRetailerClientState,
) => {
  if (isSellerInsightsSubscription(currProdType)) {
    return sellerInsightsRetailerClientState;
  } else if (isRetailKadSubscription(currProdType)) {
    return retailerClientState;
  } else {
    return hitlistsRetailerClientState;
  }
};

type ApplyTimeFramePickerProductsArgs = {
  appliedPeriodicity: Periodicity;
  appliedPeriod: BaseTimePeriod;
  appliedRelativePeriod: RelativePeriod | BaseTimePeriod;
  pagesConfig: PagesConfig;
  mainFilterStateProducts: MainFilterStateProducts;
  sideFiltersState: SideFiltersState;
  retailerClientState: RetailerClientState;
  track: any;
  setTimeFrame: (selectedPeriodicity: Periodicity) => void;
  setPeriod: (selectedOption: BaseTimePeriod) => void;
  setRelativePeriod: (selectedRelativePeriod: RelativePeriod | BaseTimePeriod) => void;
  updateMainFiltersProducts: (payload: MainFilterStateProducts) => (dispatch: any) => void;
};

/**
 * The apply time frame picker callback to invoke, which should ONLY BE USED in conjuction with the
 * TimeFramePicker within the Product page tabs.
 *
 * The main purpose for this function is to reduce duplicated code.
 */
export const applyTimeFramePickerProductsCallback = ({
  appliedPeriodicity,
  appliedPeriod,
  appliedRelativePeriod,
  pagesConfig,
  mainFilterStateProducts,
  sideFiltersState,
  retailerClientState,
  track,
  setTimeFrame,
  setPeriod,
  setRelativePeriod,
  updateMainFiltersProducts,
}: ApplyTimeFramePickerProductsArgs) => {
  applyTimeFramePicker({
    givenPeriodicity: appliedPeriodicity,
    givenPeriod: appliedPeriod,
    givenComparison: appliedRelativePeriod,
    pageType: PageType.RI_PRODUCT,
    pageName: PageName.RI_PRODUCT,
    pagesConfig: pagesConfig,
    sideFiltersState: sideFiltersState,
    retailerClientState: retailerClientState,
    track: track,
    setTimeFrame: setTimeFrame,
    setPeriod: setPeriod,
    setRelativePeriod: setRelativePeriod,
  });

  updateMainFiltersProducts({
    timeFrame: appliedPeriodicity,
    period: appliedPeriod,
    comparisonPeriod: appliedRelativePeriod,
    selectedGrowthType: mainFilterStateProducts.selectedGrowthType,
    country: mainFilterStateProducts.country,
  });
};

export const isInvalidPG = (
  timeFrame: Periodicity,
  countries: Option[],
  productGroup: Option[],
  pgListByCountry: CountryFilterPair[],
  pagesConfig: PagesConfig,
): boolean => {
  if (pagesConfig.isCountryChanged) {
    return false;
  }

  const currentPgListByCountry =
    pgListByCountry[0].country === countries[0].value ? pgListByCountry : pagesConfig.pgsByCountry;

  const invalidPGs = getInvalidPGs(timeFrame, productGroup, countries, currentPgListByCountry);
  return invalidPGs.length > 0;
};

/**
 * Update the sorting field to Absolute when the applied period is YTD or MAT and the current
 * sorting field is sorting by "Change".
 *
 * This is because there is no comparison period for YTD/MAT timefames.
 */
export const updateSortingField = (appliedPeriod: BaseTimePeriod, pagesConfig: PagesConfig) => {
  if (!isPeriodYTDOrMAT(appliedPeriod)) {
    return;
  }

  const { sortingOptionsProducts, setSortingOptionsProducts } = pagesConfig;
  if (sortingOptionsProducts.sortKey !== SortKeys.Change) {
    return;
  }

  setSortingOptionsProducts({
    ...sortingOptionsProducts,
    sortKey: SortKeys.Absolute,
    label: SortLabels.Absolute,
  });
};
